import { SyntheticEvent, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import type {
  AddressCaptureViewProps,
  AddressLookupOption,
  HandleInputChange,
} from './AddressCaptureView.types';

/**
 * The suggestion field, blind to which vendor answered. Every label, every
 * option id and every loading flag arrives already computed on `lookup`, so this
 * component contains no provider-shaped logic at all.
 */
const AddressCaptureView = ({
  lookup,
  onInputChange,
  placeholder = 'Enter street address...',
  error,
}: AddressCaptureViewProps) => {
  const {
    options,
    inputValue,
    selectedOption,
    loading,
    busy,
    setInputValue,
    onSelect,
    fetchForCurrentInput,
    onListClosed,
  } = lookup;

  const [open, setOpen] = useState(false);

  // A failed resolve must not leave a pending selection behind: with one in
  // place the customer is told to pick an address from the suggestions, which
  // blames them for something they did do — and the row they picked stays hidden
  // so they cannot even retry it.
  const resolveFailed = lookup.error?.phase === 'resolve';
  const value = resolveFailed ? null : selectedOption;

  // A failure the vendor will keep repeating — a spend ceiling, a key not allowed
  // from this URL — is escalated by the provider and swaps this field for manual
  // entry, so what reaches here is a blip and the copy invites a retry.
  const suggestFailed = lookup.error?.phase === 'suggest';
  const noOptionsText = suggestFailed
    ? 'Address lookup is temporarily unavailable. Please try again in a moment.'
    : 'No matching addresses found';

  const handleInputChange: HandleInputChange = (_, newInputValue, reason) => {
    if (reason === 'reset') return;
    setInputValue(newInputValue);
    setOpen(true);
    onInputChange?.(newInputValue);
  };

  const handleChange = (_: SyntheticEvent<Element, Event>, newValue: AddressLookupOption | null) => {
    // Picking a container narrows the search rather than producing an address,
    // so the list stays up and immediately shows what the customer drilled into.
    setOpen(!!newValue?.refinesOnSelect);
    onSelect(newValue);
  };

  const handleClose = () => {
    setOpen(false);
    onListClosed?.();
  };

  const handleOpen = () => {
    if (options.length > 0) {
      setOpen(true);
      return;
    }
    if (inputValue.trim()) fetchForCurrentInput();
  };

  const endAdornment = busy ? <CircularProgress size={20} /> : null;

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={value}
      inputValue={inputValue}
      options={options}
      loading={loading}
      noOptionsText={noOptionsText}
      filterSelectedOptions
      forcePopupIcon={false}
      // MUI's own close-on-select would fire after `handleChange` and undo the
      // drill-down decision, so open/closed is owned entirely by this component.
      disableCloseOnSelect
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(option) => option?.label ?? ''}
      filterOptions={(x) => x}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          placeholder={placeholder}
          error={!!error}
          InputProps={{
            ...params.InputProps,
            endAdornment,
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            // The design carries no visible label, so the placeholder doubles as
            // the accessible name. It is also what makes the service-address and
            // billing-address fields distinguishable, since they are this same
            // component rendered twice with different wording.
            'aria-label': placeholder,
          }}
          FormHelperTextProps={{
            component: FormErrorMessage,
          }}
          helperText={error || ''}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Typography variant="body.md">{option.label}</Typography>
        </li>
      )}
    />
  );
};

export default AddressCaptureView;
